<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { stringToMinutes } from '@/utils/helpers';
import { isToday } from 'date-fns';
import { cloneDeep } from 'lodash';

const emit = defineEmits(['deselect', 'time-selected']);

const props = defineProps({
  selectedDate: {
    type: String,
    default: '',
  },
  startTime: {
    type: String,
    default: '00:00',
  },
  endTime: {
    type: String,
    default: '23:45',
  },
  type: {
    type: String,
    default: 'start',
  },
  color: {
    type: String,
    default: '',
  },
  timeSlots: {
    type: Array,
    default: () => [],
  },
});

const slots = ref([]);
const timeSelected = ref(false);

const allSlots = computed(() => {
  const sameDay = isToday(new Date(props.selectedDate), new Date());
  const startInMinutes = stringToMinutes(props.startTime);
  const endInMinutes = stringToMinutes(props.endTime);

  return cloneDeep(props.timeSlots).filter((slot) => {
    const slotInMinutes = stringToMinutes(slot.time);
    if (endInMinutes < slotInMinutes) return false;
    if (slotInMinutes < startInMinutes) return false;
    return sameDay ? slotInMinutes >= startInMinutes : true;
  });
});

watch(
  () => props.selectedDate,
  () => {
    slots.value = cloneDeep(allSlots.value);
  }
);

watch(
  () => props.timeSlots,
  (newVal) => {
    if (props.type === 'end') {
      slots.value = newVal;
    }
  }
);

onMounted(() => {
  slots.value = cloneDeep(allSlots.value);
});

function selectTime(slot, index) {
  if (!slot.available && props.type === 'start') return;

  timeSelected.value = true;

  if (slot.selected) {
    slots.value = cloneDeep(allSlots.value);
    timeSelected.value = false;
    emit('deselect');
    return;
  }

  slots.value = slots.value.splice(index, 1);
  slot.selected = true;

  document.querySelector('.backdrop').scrollTop = 0;

  emit('time-selected', slot.time);
}
</script>

<template>
  <div class="animate transition-all">
    <p class="h800 mb-4">
      {{ props.type === 'end' ? $t('global.endTime') : $t('global.startTime') }}
    </p>
    <TransitionGroup v-if="timeSlots.length" name="list" tag="ul">
      <li
        v-for="(slot, index) in slots"
        :key="index"
        class="bg-neutral-0 border border-neutral-500 border-sm rounded-2xl h-20 mb-4 flex items-center justify-center"
        :class="{ 'opacity-50 line-through': !slot.available }"
        @click="selectTime(slot, index)"
      >
        <p class="h800">
          {{ slot.time }}
        </p>
      </li>
    </TransitionGroup>
  </div>
</template>

<style scoped lang="scss">
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
